import React, { useEffect, useState } from "react";
import MembershipPanelView from "presentational/membership/MembershipPanelView";
import FullScreenLoadingView from "presentational/common/FullScreenLoadingView";
import TXT from "presentational/common/multinational/TXT";
import GaHelper from "lib/gaHelper";
import { sleep } from "lib/commonUtil";
import { useHistory } from "react-router-dom";
import { withStore } from "store/storeHandler";
import { withToken } from "lib/forceTokenRenewComponent";
import {
  deletePriceInfo,
  getImmutablePriceInfo
} from "lib/membershipPriceInfo";
import Restful from "lib/restful";
import { getEssentialUrl } from "lib/urlQueryParser";
import PropTypes from "prop-types";

RootMembership.propTypes = {
  query: PropTypes.object.isRequired
};

function RootMembership(props) {
  const {
    query,
    membershipInfo,
    username,
    MyAccountActions,
    AppErrorActions,
    NoticeModalActions
  } = props;
  const { forceRenewTokenWT, getIdTokenWT } = props;
  const [isLoading, setIsLoading] = useState(false);

  const history = useHistory();

  const priceInfo = getImmutablePriceInfo();

  useEffect(() => {
    refreshMembershipInfo();
    sendRedirectedPortoneData();

    return () => {
      deletePriceInfo();
    };
  }, []);

  const isValidToken = async () => {
    const hasSucceed = await forceRenewTokenWT();
    return hasSucceed;
  };

  const refreshMembershipInfo = async () => {
    const returnObj = { isChanged: false, newMembershipInfo: null, fail: null };

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) {
      returnObj.fail = "tokenExpired";
      return returnObj;
    }

    try {
      const { isChanged, newMembershipInfo } =
        await MyAccountActions.forceUpdateMembershipData(idToken);
      returnObj.isChanged = isChanged;
      returnObj.newMembershipInfo = newMembershipInfo;
    } catch (errObj) {
      AppErrorActions.setFatalAppError(errObj);
      returnObj.fail = "refreshError";
    }

    return returnObj;
  };

  const catchPortonePaymentError = ({ error_msg, imp_uid }) => {
    /**
     * 각 pg사의 사용자가 취소시 오류메시지
     * 이니시스(모바일) : "F0004:PG사 결제요청에 실패하여 중단합니다.(imp_775523558882) [코드: 01] [cancel] 사용자에 의한 취소입니다."
     * 이니시스(PC) : "사용자가 결제를 취소하셨습니다"
     * KCP(PC/모바일), 카카오페이(PC/모바일) : "[결제포기] 사용자가 결제를 취소하셨습니다"
     * ---------------------------------
     * KCP로 모바일(IOS)에서 결제완료후(사용자 포트폴리오 화면으로 자동진입 하게됨)
     * -> 백 수행(하면 멤버십화면으로 이동함)
     * -> 다시 백 수행(하면 KCP결제화면으로 진입후 바로 본 함수로 진입하게 됨)
     *  : "F0002:이미 결제가 이루어진 거래건입니다(imp_544535458158). status:paid"
     */
    if (
      error_msg.indexOf("사용자가 결제를 취소하셨습니다") < 0 &&
      error_msg.indexOf("사용자에 의한 취소입니다") < 0 &&
      error_msg.indexOf("이미 결제가 이루어진 거래건입니다") < 0
    ) {
      const errObj = Error(`portone payment error`);
      errObj.errorValue = { imp_uid, error_msg };
      AppErrorActions.setSafeAppError(errObj);

      const content = <TXT name="msg_portoneServerError" />;
      displayModal({ content });
    }
  };

  const sendRedirectedPortoneData = async () => {
    /**
     * 카카오페이, kcp, 이니시스중
     * 카카오페이, kcp 두가지 pg사만 모바일 결제후 완료 혹은 에러발생시
     * 리다이렉트되어 query 객체와 함께 이 함수를 호출한다.
     * query = {
     *   error_msg,
     *   imp_success, [string] ("true", "false", undefined)
     *   imp_uid,
     *   merchant_uid
     * }
     */
    const { imp_success, imp_uid } = query;

    if (typeof imp_success === "undefined") return null;

    history.replace(getEssentialUrl({ type: "membership" })());

    const success = imp_success === "true" ? true : false;

    if (success) {
      await handleSubscribeViaPortone();
    } else {
      const { error_msg } = query;
      catchPortonePaymentError({ error_msg, imp_uid });
    }
  };

  const isLatestMembershipInfo = async () => {
    setIsLoading(true);

    if (!(await isValidToken())) {
      setIsLoading(false);
      return false;
    }

    const { isChanged, fail } = await refreshMembershipInfo();
    setIsLoading(false);

    if (fail) {
      return false;
    }

    if (isChanged) {
      return false;
    }
    return true;
  };

  const goPortpolioPage = () => {
    history.push(getEssentialUrl({ type: "portfolio" })(username));
  };

  const displayModal = ({ content, callback }) => {
    NoticeModalActions.openModal({ content, callback });
  };

  const displaySubscribeSuccessModal = () => {
    const content = <TXT name="msg_registrationCompleted" />;
    const callback = goPortpolioPage;
    displayModal({ content, callback });
  };

  const displayRefundRefusalReason = ({ reason }) => {
    let content;
    switch (reason) {
      case "NonPayingUser":
        content = <TXT name="msg_nonPayingUser" />;
        break;
      case "CouponUser":
        content = <TXT name="msg_couponUser" />;
        break;
      case "RefundExpired":
        content = <TXT name="msg_refundExfired" />;
        break;
      default:
        const errObj = Error(`unknown reason type`);
        errObj.errorValue = { reason };
        AppErrorActions.setSafeAppError(errObj);
        return null;
    }

    displayModal({ content });
  };

  const requestToBackendToStartFreeTrial = async idToken => {
    const { code, errInfo } = await Restful.setFreeTrialAPI({ idToken });

    await refreshMembershipInfo();

    if (code === "ok") {
      return null;
    } else if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
    } else {
      let content;
      switch (code) {
        case "AlreadyAppliedForMembership":
        case "CannotRequestFreeTrialTwice":
          content = <TXT name="msg_cannotStartFreeTrial" />;
          break;
        case "AlreadyFreeTrialUser":
          content = <TXT name="msg_nowInFreeTrial" />;
          break;
        case "UserLeavingSoon":
          content = <TXT name="msg_accountWillDeletedSoon" />;
          break;
        default:
          const errObj = Error(`unknown code`);
          errObj.errorValue = { code };
          AppErrorActions.setSafeAppError(errObj);
          return null;
      }
      displayModal({ content });
    }
  };

  const requestToBackendToSubscribePaypal = async (idToken, paypalSubId) => {
    const { code, errInfo } = await Restful.setPaidMembershipAPI({
      idToken,
      data: { paypalSubId, paymentType: "paypal" }
    });

    await refreshMembershipInfo();

    if (code === "ok") {
      displaySubscribeSuccessModal();
    } else if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
    } else {
      let content;
      switch (code) {
        case "PaypalInfoRequestError":
        case "PaypalDoesntApproved":
          content = <TXT name="msg_paypalPaymentError" />;
          break;
        case "PaypalActivateError":
        case "PaypalInfoUpdateError":
          content = <TXT name="msg_paypalRequestError" />;
          break;
        case "AlreadyJoinedMembership":
          content = <TXT name="msg_alreadyMember" />;
          break;
        case "UserLeavingSoon":
          content = <TXT name="msg_accountWillDeletedSoon" />;
          break;
        default:
          const errObj = Error(`unknown code`);
          errObj.errorValue = { code };
          AppErrorActions.setSafeAppError(errObj);
          return null;
      }
      displayModal({ content });
    }
  };

  const requestToBackendToCancelPaypal = async idToken => {
    const { code, errInfo } = await Restful.cancelPaidMembershipAPI({
      data: { paymentType: "paypal" },
      idToken
    });

    await refreshMembershipInfo();

    if (code === "ok") {
      return null;
    } else if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
    } else {
      let content;
      switch (code) {
        case "PaypalInfoRequestError":
        case "PaypalCancelError":
          content = <TXT name="msg_paypalRequestError" />;
          break;
        case "InfoOfPaypalDoesNotExist":
          content = <TXT name="msg_notRegisteredMember" />;
          break;
        case "AlreadyCancelled":
          content = <TXT name="msg_alreadyUnsubscribed" />;
          break;
        default:
          const errObj = Error(`unknown code`);
          errObj.errorValue = { code };
          AppErrorActions.setSafeAppError(errObj);
          return null;
      }
      displayModal({ content });
    }
  };

  const requestToBackendToCancelPortone = async idToken => {
    const { code, errInfo } = await Restful.cancelPaidMembershipAPI({
      data: { paymentType: "portone" },
      idToken
    });

    await refreshMembershipInfo();

    if (code === "ok") {
      return null;
    } else if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
    } else {
      let content;
      switch (code) {
        case "PortoneServerError":
          content = <TXT name="msg_portoneServerError" />;
          break;
        case "InfoOfMembershipDoesNotExist":
          content = <TXT name="msg_notRegisteredMember" />;
          break;
        case "AlreadyCancelled":
          content = <TXT name="msg_alreadyUnsubscribed" />;
          break;
        default:
          const errObj = Error(`unknown code`);
          errObj.errorValue = { code };
          AppErrorActions.setSafeAppError(errObj);
          return null;
      }
      displayModal({ content });
    }
  };

  const requestToBackendToRefund = async idToken => {
    const { code, res, errInfo } = await Restful.refundPaidMembershipAPI({
      idToken
    });

    await refreshMembershipInfo();

    if (code === "ok") {
      const { hasRefunded, reason } = res;
      if (!hasRefunded) {
        displayRefundRefusalReason({ reason });
      }
    } else if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
    } else {
      let content;
      switch (code) {
        case "PortoneServerError":
          content = <TXT name="msg_portoneServerError" />;
          break;
        case "UnknownTransactionID":
          content = <TXT name="msg_paypalRequestError" />;
          break;
        default:
          const errObj = Error(`unknown code`);
          errObj.errorValue = { code };
          AppErrorActions.setSafeAppError(errObj);
          return null;
      }
      displayModal({ content });
    }
  };

  const submitToBackend = async (submitType, optObj) => {
    if (isLoading) return null;

    if (!membershipInfo) {
      refreshMembershipInfo();
      return null;
    }

    await setIsLoading(true);

    if (!(await isValidToken())) {
      setIsLoading(false);
      return null;
    }

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) {
      setIsLoading(false);
      return null;
    }

    switch (submitType) {
      case "startFreeTrial":
        await requestToBackendToStartFreeTrial(idToken);
        break;
      case "subscribeViaPaypal":
        const { paypalSubId } = optObj;
        await requestToBackendToSubscribePaypal(idToken, paypalSubId);
        break;
      case "unsubscribe":
        const { paymentType } = membershipInfo;
        if (paymentType === "paypal") {
          await requestToBackendToCancelPaypal(idToken);
        } else if (paymentType === "portone") {
          await requestToBackendToCancelPortone(idToken);
        } else {
          const errObj = Error(`wrong paymentType`);
          errObj.errorValue = { paymentType };
          AppErrorActions.setSafeAppError(errObj);
        }
        break;
      case "refund":
        await requestToBackendToRefund(idToken);
        break;
      default:
        const errObj = Error(`wrong submitType`);
        errObj.errorValue = { submitType };
        AppErrorActions.setSafeAppError(errObj);
        break;
    }

    setIsLoading(false);
  };

  const sendGAEvent = eventType => {
    switch (eventType) {
      case "subscribe":
        GaHelper.setGAEvent({ type: "ma_subscribe_membership" });
        return null;
      case "startFreeTrial":
        GaHelper.setGAEvent({ type: "ma_start_free_trial" });
        return null;

      default:
        console.error(`wrong eventType : "${eventType}"`);
        return null;
    }
  };

  const handleRequestFreeTrial = async () => {
    await submitToBackend("startFreeTrial");
    goPortpolioPage();

    sendGAEvent("startFreeTrial");
  };

  const handleSubscribeViaPaypal = async paypalSubId => {
    const optObj = { paypalSubId };
    await submitToBackend("subscribeViaPaypal", optObj);

    sendGAEvent("subscribe");
  };

  const handleSubscribeViaPortone = async () => {
    if (isLoading) return null;

    const WAIT_MILLISEC = 2000;

    await setIsLoading(true);

    for (let i = 0; i < 5; i++) {
      await sleep(WAIT_MILLISEC);
      const { newMembershipInfo, fail } = await refreshMembershipInfo();

      if (fail) {
        setIsLoading(false);
        return null;
      }

      if (newMembershipInfo.userType === "personal") {
        displaySubscribeSuccessModal();
        break;
      }
    }

    setIsLoading(false);

    sendGAEvent("subscribe");
  };

  const handleSubmitUnsubscription = async () => {
    await submitToBackend("unsubscribe");
  };

  const handleSubmitRefund = async () => {
    await submitToBackend("refund");
  };

  const handleRequestRefundInfo = async () => {
    if (isLoading) return { refundable: false };

    await setIsLoading(true);

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) {
      setIsLoading(false);
      return { refundable: false };
    }

    const { code, res, errInfo } =
      await Restful.getRefundInfoForPaidMembershipAPI({
        idToken
      });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
      setIsLoading(false);
      return { refundable: false };
    }

    setIsLoading(false);

    const { isRefundable, reason } = res;
    if (isRefundable) {
      return { refundable: true };
    } else {
      displayRefundRefusalReason({ reason });
      return { refundable: false };
    }
  };

  return (
    <div>
      {isLoading && <FullScreenLoadingView />}
      <MembershipPanelView
        membershipInfo={membershipInfo}
        priceInfo={priceInfo}
        onRequestFreeTrial={handleRequestFreeTrial}
        onSubscribeViaPaypal={handleSubscribeViaPaypal}
        onSubscribeViaPortone={handleSubscribeViaPortone}
        onSubmitUnsubscription={handleSubmitUnsubscription}
        onRequestRefundInfo={handleRequestRefundInfo}
        onSubmitRefund={handleSubmitRefund}
        isLatestMembershipInfo={isLatestMembershipInfo}
        catchPortonePaymentError={catchPortonePaymentError}
      />
    </div>
  );
}

export default withStore({
  stateMap: { myAccount: ["membershipInfo", "username"] },
  dispatchMap: ["appError", "myAccount", "noticeModal"]
})(withToken(RootMembership));
